/**
 * Таксономия отказов единицы работы (P3 ревью 2026-10-18: «одна модель работы с попытками»).
 *
 * ЗАЧЕМ ОДИН СЛОВАРЬ НА ТРИ СЛУЖБЫ. До этого файла отказ классифицировал каждый сам: писатель —
 * по своим классам ошибок (`agent-writer/src/llm/errors.ts`), переделка — своим списком
 * «неизлечимого» (`rework.ts: terminalReason`), мониторинг — никак. Итог — один и тот же
 * mid-stream `overloaded_error` был «формой запроса» у переделки и навсегда хоронил её, а
 * усечённая серия у темы считалась временной и оплачивалась каждые 15 минут бесконечно.
 *
 * РАЗДЕЛЕНИЕ ТРУДА:
 *   · СЕРВИС знает, ЧТО случилось, — `WorkFailureKind`, закрытый словарь, по типу ошибки, а не
 *     по тексту сообщения;
 *   · КОНТРАКТ знает, к какому КЛАССУ это относится (`failureClassOf`), — одна таблица ниже;
 *   · БЭКЕНД решает, что с этим делать: сколько попыток, через сколько, терминально ли
 *     (`services/backend/src/domain/work-items.ts`). Сервис политику не выбирает: он только
 *     сообщает вид отказа маршруту, который освобождает работу.
 *
 * ЧЕТЫРЕ КЛАССА, и у каждого одна реакция:
 *   · transient     — пройдёт само (перегрузка, 5xx, сеть, 429): повтор с растущей паузой, но
 *                     не больше N раз;
 *   · deterministic — повтор даст то же самое за те же деньги (обрыв по max_tokens, 4xx формы
 *                     запроса, несобираемая версия, политика ссылок): терминально с первого раза;
 *   · budget        — стоп на уровне аккаунта или бренда (квота черновиков, ключ, биллинг):
 *                     работа ждёт, попытка не засчитывается;
 *   · refusal       — отказ модели (`stop_reason: refusal`): терминально, на аккаунт ложится
 *                     каждый повтор.
 *
 * Модуль чистый: без SDK Anthropic и без `node:*` (правило R8 входа `.`). SDK-ошибки он
 * разбирает ПО ФОРМЕ — `status`, `type`, тип тела и имена классов в цепочке прототипов, — ровно
 * теми полями, что SDK объявляет публично (`@anthropic-ai/sdk/core/error`).
 */

import { parseFeedDate } from './time.ts';

export const FAILURE_CLASSES = ['transient', 'deterministic', 'budget', 'refusal'] as const;
export type FailureClass = (typeof FAILURE_CLASSES)[number];

/**
 * Вид отказа. Закрытый словарь: на проводе (`failure.kind` маршрутов освобождения работы)
 * неизвестное значение — 400, а не молча «временный».
 */
export const WORK_FAILURE_KINDS = [
  // ── вызов модели
  /** 429 после ретраев SDK (`rate_limit_error`). */
  'rate_limit',
  /** 529 или SSE-событие `overloaded_error` посреди потока. */
  'overloaded',
  /** 5xx или SSE-событие `api_error`. */
  'server',
  /** Сеть: обрыв соединения, отказ DNS, ответ без статуса. */
  'connection',
  /** Таймаут запроса к модели. */
  'timeout',
  /** Запрос оборвали мы сами (останов процесса). */
  'aborted',
  /** Настоящий 4xx формы запроса (400/404/413/422): повтор даст тот же отказ. */
  'request',
  /** 401/403: ключ отозван или прав нет. Работа ждёт человека, а не следующей попытки. */
  'auth',
  /** Биллинг аккаунта (`billing_error`). */
  'billing',
  /** `stop_reason: refusal`. */
  'refusal',
  /** `stop_reason: max_tokens`: вызов оплачен, разбирать нечего. */
  'truncated',
  // ── конвейер писателя
  /** Ни одна версия не собралась / ответ не разобрался по схеме после починки. */
  'build',
  /**
   * Штатный ответ (`end_turn`) не прошёл zod-схему вывода без починки. Строгий вывод провайдера
   * держит форму JSON, но не границы строк (`min`/`max`): промах — случайность выборки, и
   * следующий вызов обычно проходит. Поэтому временный, а не `build`.
   */
  'schema_miss',
  /** 422 `link_policy_violation` на приёме версии. */
  'link_policy',
  /** 422/400 тела версии на приёме (ошибка клиента). */
  'invalid_request',
  /** Дайджест недели, форма которого не починилась. */
  'digest_unfit',
  /** Суточная квота черновиков бренда. */
  'quota',
  /** intake не ответил (сеть, 5xx бэкенда). */
  'intake',
  /** Стиль версии недоступен отказом бэкенда. */
  'style_unavailable',
  /** Переделка картинки: вердикт состоялся и сказал «рисовать нечего». */
  'visual_none',
  /** Переделка картинки: рендер карточки отказал (тот же макет даст тот же отказ). */
  'render',
  /**
   * Мнение автора канала (AGI Boardroom, `api/author-opinion.ts`) не пережило переделку: модель
   * не поставила токен места, очистка не сошлась к форме без адресов, или очищенный текст не
   * нашёлся в собранной версии дословно. Случайность выборки, как `schema_miss`, — временный:
   * похоронить просьбу автора после одного неудачного ответа значило бы молча её потерять.
   */
  'opinion_lost',
  // ── заявки
  /** Темы для заявки нет: мониторинг ничего не дал, тема снята, очередь пуста. */
  'no_topic',
  /** Ожидающий (писатель) не дождался мониторинга и сдался. */
  'monitoring_timeout',
  /** Исполнитель пропал: взятая работа без сигнала жизни дольше окна молчания. */
  'stale',
  /** Исполнитель перезапущен посреди работы и отпустил её сам. */
  'restarted',
  /** Не классифицировано. Считается временным: похоронить неизвестное дороже, чем повторить. */
  'unknown',
] as const;
export type WorkFailureKind = (typeof WORK_FAILURE_KINDS)[number];

export const isWorkFailureKind = (v: string): v is WorkFailureKind =>
  (WORK_FAILURE_KINDS as readonly string[]).includes(v);

const CLASS_OF: Readonly<Record<WorkFailureKind, FailureClass>> = {
  rate_limit: 'transient',
  overloaded: 'transient',
  server: 'transient',
  connection: 'transient',
  timeout: 'transient',
  aborted: 'transient',
  request: 'deterministic',
  auth: 'budget',
  billing: 'budget',
  refusal: 'refusal',
  truncated: 'deterministic',
  build: 'deterministic',
  schema_miss: 'transient',
  link_policy: 'deterministic',
  invalid_request: 'deterministic',
  digest_unfit: 'deterministic',
  quota: 'budget',
  intake: 'transient',
  style_unavailable: 'deterministic',
  visual_none: 'deterministic',
  render: 'deterministic',
  opinion_lost: 'transient',
  no_topic: 'deterministic',
  monitoring_timeout: 'deterministic',
  stale: 'transient',
  restarted: 'transient',
  unknown: 'transient',
};

export function failureClassOf(kind: WorkFailureKind): FailureClass {
  return CLASS_OF[kind];
}

/** Итог разбора SDK-ошибки: вид и подсказка провайдера, когда повторять. */
export interface LlmFailureVerdict {
  readonly kind: WorkFailureKind;
  readonly class: FailureClass;
  /** `retry-after` провайдера в мс, если он его прислал; `null` — решает политика бэкенда. */
  readonly retryAfterMs: number | null;
}

/** Потолок подсказки `retry-after`: дальше суток подсказка — мусор, а не план. */
const RETRY_AFTER_MAX_MS = 86_400_000;

/** Тип ошибки тела Anthropic (`{type:'error', error:{type}}`) → вид отказа. */
const KIND_BY_ERROR_TYPE: Readonly<Record<string, WorkFailureKind>> = {
  overloaded_error: 'overloaded',
  api_error: 'server',
  rate_limit_error: 'rate_limit',
  timeout_error: 'timeout',
  invalid_request_error: 'request',
  not_found_error: 'request',
  request_too_large: 'request',
  authentication_error: 'auth',
  permission_error: 'auth',
  billing_error: 'billing',
};

function own(o: unknown, key: string): unknown {
  return (typeof o === 'object' && o !== null) || typeof o === 'function'
    ? (o as Record<string, unknown>)[key]
    : undefined;
}

/** Имена классов вдоль цепочки прототипов: `APIConnectionTimeoutError` → `APIConnectionError` → … */
function classNames(e: object): string[] {
  const out: string[] = [];
  for (let p: unknown = Object.getPrototypeOf(e); p !== null && p !== undefined; ) {
    const name = own(own(p, 'constructor'), 'name');
    if (typeof name === 'string') out.push(name);
    p = Object.getPrototypeOf(p);
  }
  return out;
}

/** Тип ошибки: собственное поле SDK `type`, иначе тело `{error:{type}}` либо `{type}`. */
function errorTypeOf(e: object): string | null {
  const direct = own(e, 'type');
  if (typeof direct === 'string' && direct !== '') return direct;
  const body = own(e, 'error');
  const nested = own(own(body, 'error'), 'type');
  if (typeof nested === 'string') return nested;
  const flat = own(body, 'type');
  return typeof flat === 'string' && flat !== 'error' ? flat : null;
}

/** `retry-after` (секунды или HTTP-дата) и `retry-after-ms` из заголовков SDK-ошибки. */
function retryAfterOf(e: object, nowMs: number | null): number | null {
  const headers = own(e, 'headers');
  const read = (name: string): string | null => {
    const get = own(headers, 'get');
    if (typeof get === 'function') {
      const v = (get as (n: string) => unknown).call(headers, name);
      return typeof v === 'string' ? v : null;
    }
    const v = own(headers, name);
    return typeof v === 'string' ? v : null;
  };
  const ms = read('retry-after-ms');
  if (ms !== null && /^\d+(\.\d+)?$/.test(ms)) {
    return Math.min(Math.round(Number(ms)), RETRY_AFTER_MAX_MS);
  }
  const raw = read('retry-after');
  if (raw === null) return null;
  if (/^\d+(\.\d+)?$/.test(raw))
    return Math.min(Math.round(Number(raw) * 1000), RETRY_AFTER_MAX_MS);
  if (nowMs === null) return null;
  // HTTP-дата — форма RFC 822; разбор дат живёт только в `time.ts` (правило R11).
  const at = parseFeedDate(raw);
  return at === null ? null : Math.min(Math.max(0, at.ms - nowMs), RETRY_AFTER_MAX_MS);
}

function verdict(kind: WorkFailureKind, retryAfterMs: number | null = null): LlmFailureVerdict {
  return { kind, class: failureClassOf(kind), retryAfterMs };
}

/**
 * Разбор ошибки вызова модели по форме SDK Anthropic.
 *
 * ГЛАВНЫЙ СЛУЧАЙ, РАДИ КОТОРОГО ФАЙЛ И НАПИСАН: SSE-событие `error` посреди потока. SDK бросает
 * `new APIError(undefined, body, undefined, headers, type)` — статуса НЕТ, и правило «4xx или
 * неизвестный статус — форма запроса» хоронило перегрузку провайдера как баг клиента. Ошибка
 * без статуса классифицируется по типу тела: `overloaded_error`/`api_error` — временные; тип,
 * которого нет в таблице, — сеть (тоже временная). Форма запроса — только настоящий 4xx.
 *
 * `nowMs` нужен ровно одному случаю — `retry-after` в виде HTTP-даты; часы сюда передают
 * снаружи (правило R11: своих часов у модуля нет).
 */
export function classifyLlmFailure(e: unknown, nowMs: number | null = null): LlmFailureVerdict {
  if (typeof e !== 'object' || e === null) return verdict('unknown');
  const names = classNames(e);
  // Только ошибки SDK: посторонний объект с полем `status: 429` — не 429 провайдера, и
  // принять его за 429 значило бы классифицировать по совпадению формы, а не по происхождению.
  if (!names.includes('APIError')) {
    return verdict(own(e, 'name') === 'AbortError' ? 'aborted' : 'unknown');
  }
  const status = own(e, 'status');
  const type = errorTypeOf(e);

  if (typeof status === 'number') {
    if (status === 429) return verdict('rate_limit', retryAfterOf(e, nowMs));
    if (status === 529) return verdict('overloaded', retryAfterOf(e, nowMs));
    if (status === 408) return verdict('timeout');
    if (status >= 500) return verdict(type === 'overloaded_error' ? 'overloaded' : 'server');
    if (status === 401 || status === 403) return verdict('auth');
    if (status >= 400) {
      const byType = type === null ? undefined : KIND_BY_ERROR_TYPE[type];
      return verdict(byType === 'billing' ? 'billing' : 'request');
    }
    return verdict('server');
  }

  // Без статуса: сначала собственные классы SDK (порядок значим — таймаут наследник соединения).
  if (names.includes('APIUserAbortError')) return verdict('aborted');
  if (names.includes('APIConnectionTimeoutError')) return verdict('timeout');
  if (names.includes('APIConnectionError')) return verdict('connection');
  if (type !== null) {
    const byType = KIND_BY_ERROR_TYPE[type];
    if (byType !== undefined) return verdict(byType, retryAfterOf(e, nowMs));
  }
  // APIError без статуса и без знакомого типа — обрыв потока на стороне сети/прокси.
  return verdict('connection');
}
